/**
 * Module 64 (WP-64.A1) — deterministic pool→index scheduler.
 *
 * Relocated verbatim from `@puzzlemania/background-gen`'s `assign.ts`
 * (Module 34, WP-34.1), where it had nothing background-specific in it: it is a
 * pure "pick an id from an ordered pool for an index" function. Module 64 gives
 * Block Jam a pool of music tracks and needs the same four guarantees, and
 * music must not import from a package named `background-gen`.
 *
 * The behaviour is UNCHANGED and that is asserted, not assumed:
 * `background-gen/src/assign-relocation.test.ts` pins every schedule for pool
 * sizes 1–12 over 200 indices against a fixture captured from the pre-move
 * implementation. Every player's background sequence depends on this function,
 * and a silent reshuffle is invisible to every other test in the repo.
 *
 * The brief's §9 rules, in priority order, degrading gracefully for small pools:
 *   1. no consecutive repeat        — pick(i) ≠ pick(i-1)                (P ≥ 2)
 *   2. no repeat within a window     — of `min(5, P-1)` preceding indices (P ≥ 6 ⇒ 5)
 *   3. even distribution             — per-id counts within ±1 over whole cycles
 *   4. stable mapping                — deterministic, re-run equal
 *
 * Mechanism: seeded **shuffled cycles** through the pool. Each cycle is a
 * permutation of all P ids, so every id appears exactly once per cycle (⇒ even
 * distribution). Within a cycle all ids are distinct, so any window of size
 * ≤ P-1 is automatically satisfied; a greedy per-position picker repairs the
 * cycle **boundaries** so the window rule also holds across them. The window
 * of size K = min(5, P-1) subsumes rules 1 and 2.
 *
 * Nothing is persisted: the schedule is a pure function of pool size and index,
 * which is exactly what makes it survive a reload.
 */
/**
 * Deliberately a PRIVATE copy of `background-gen`'s `mulberry32`, not an import
 * of it and not a re-export back into it.
 *
 * `packages/shared` is a leaf with no dependencies and stays one. More
 * importantly, `background-gen`'s `rng.ts` also seeds `generate()` — the
 * procedural background generator — and routing that through a second package
 * would put a far larger, unrelated surface at risk for the sake of removing
 * eight lines. `rng.ts` is therefore untouched by this module, which is what
 * makes "generation is unaffected" true by construction rather than by test.
 *
 * Do not "de-duplicate" these two. They are the same eight lines for different
 * reasons, and the copy here is load-bearing for the isolation above.
 */
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Fixed internal seed ⇒ deterministic schedule (only pool size varies it). */
const INTERNAL_SEED = 0x9e3779b9;
/**
 * Build the assignment schedule for a pool of `poolSize` ids: an array of
 * length `length` of pool indices (0..poolSize-1). Deterministic in
 * `poolSize`; the prefix is identical regardless of the requested `length`,
 * so `buildPoolSchedule(P, n)` and `buildPoolSchedule(P, m)` agree on their
 * common range (⇒ `pickFromPool` is stable across ranges).
 */
export function buildPoolSchedule(poolSize, length, windowSize) {
    const len = Math.max(0, Math.floor(length));
    if (poolSize <= 0 || len === 0)
        return [];
    const P = poolSize;
    // P = 1: only one choice; rules 1 & 2 cannot be honoured — constant.
    if (P === 1)
        return new Array(len).fill(0);
    // How many preceding picks may not repeat. The default is Module 34's
    // `min(5, P-1)`, unchanged — every background call site relies on it and
    // `assign-relocation.test.ts` pins it.
    //
    // It is a PARAMETER because the default has a property that only shows up on
    // small pools: when `K >= P-1` the window forbids everything but one
    // candidate, so the "shuffled cycle" degenerates into a FIXED cycle with no
    // randomness at all. That happens for every pool of 6 or fewer. Backgrounds
    // have 9 and never meet it; a music pool of three or four meets it always,
    // and would rotate 1,2,3,1,2,3 forever.
    //
    // So a caller that wants genuine variety on a small pool passes a smaller
    // window — `1` still guarantees "never the track just heard" and still leaves
    // distribution even (each cycle is a full permutation), while restoring real
    // choice inside the cycle.
    const K = Math.max(1, Math.min(windowSize === undefined ? Math.min(5, P - 1) : Math.floor(windowSize), P - 1));
    const rng = mulberry32((INTERNAL_SEED ^ Math.imul(P, 0x85ebca6b)) >>> 0);
    const seq = [];
    while (seq.length < len) {
        // One cycle = a permutation of all P ids, drained from `bag`.
        const bag = [];
        for (let i = 0; i < P; i++)
            bag.push(i);
        for (let j = 0; j < P && seq.length < len; j++) {
            const windowStart = Math.max(0, seq.length - K);
            const forbidden = new Set(seq.slice(windowStart));
            // Candidates in `bag` not seen in the last K positions. Because each
            // cycle is a fresh full permutation and K ≤ P-1, at least one candidate
            // always remains — the picker never deadlocks.
            const allowed = [];
            for (let b = 0; b < bag.length; b++) {
                if (!forbidden.has(bag[b]))
                    allowed.push(b);
            }
            const pickAt = allowed[Math.floor(rng() * allowed.length)];
            seq.push(bag[pickAt]);
            bag.splice(pickAt, 1);
        }
    }
    return seq;
}
/**
 * Pick an entry from `pool` for a **0-based** `index`.
 *
 * `windowSize` is forwarded to {@link buildPoolSchedule}; omit it for Module
 * 34's original behaviour, and see that function for why a small pool wants a
 * smaller window.
 *
 * Zero-based on purpose: this function knows nothing about levels. Callers that
 * count from 1 (levels) or bucket several of them together (Module 64's music
 * stride) do that arithmetic themselves and hand over a plain index.
 *
 * Throws on an empty pool — callers with an empty pool must fall back to their
 * own default rather than calling this.
 */
export function pickFromPool(pool, index, windowSize) {
    if (!pool || pool.length === 0) {
        throw new Error('pickFromPool: pool must be non-empty');
    }
    const i = Math.max(0, Math.floor(index));
    const schedule = buildPoolSchedule(pool.length, i + 1, windowSize);
    return pool[schedule[i]];
}
